import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Row, Col, Card, Spin, Empty, DatePicker, Select, Button, Icon, Modal, Table, Input, InputNumber, Form } from 'antd';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { getAllMovements, getMovements, getAllExpensesGlobal, addExpense, getSupervisors, updateMovement, deleteMovementRecord } from '../../../../../../services/apiaryService';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

// --- Styled Components ---

const MainContainer = styled.div`
    padding: ${props => props.isGlobal ? '30px' : '20px 0'};
    background: ${props => props.isGlobal ? '#f8f9fa' : 'transparent'};
    min-height: ${props => props.isGlobal ? '100vh' : 'auto'};
    margin-left: ${props => props.isGlobal ? '15%' : '0'};
    width: ${props => props.isGlobal ? '85%' : '100%'};

    @media (max-width: 500px) {
        margin-left: 0;
        width: 100%;
        padding: 15px;
    }
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    
    h2 {
        font-size: 24px;
        font-weight: 800;
        color: #2d3436;
        margin: 0;
    }
    
    .badge-count {
        background: #ff8701;
        color: white;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 13px;
        font-weight: 700;
        margin-left: 10px;
        vertical-align: middle;
    }
`;

const FilterCard = styled.div`
    background: #fff;
    padding: 20px;
    border-radius: 12px;
    margin-bottom: 25px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.02);
    border: 1px solid #eaeaea;
`;

const TableWrapper = styled.div`
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.02);
    overflow: hidden;
    border: 1px solid #eaeaea;
    padding: 20px;

    .ant-table-thead > tr > th {
        background: #f8f9fa;
        color: #2d3436;
        font-weight: 700;
        text-transform: uppercase;
        font-size: 12px;
        letter-spacing: 0.5px;
        border-bottom: 2px solid #ff8701;
    }

    .ant-table-tbody > tr > td {
        font-size: 14px;
        color: #2d3436;
    }
`;

const ExpenseLink = styled.span`
    color: #ff8701;
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;
    
    &:hover {
        color: #e07200;
    }
`;

const DetailModalHeader = styled.div`
    border-bottom: 1px solid #f0f0f0;
    padding-bottom: 15px;
    margin-bottom: 20px;
    
    h3 {
        margin: 0 0 5px 0;
        color: #2d3436;
        font-weight: 800;
    }
    
    p {
        margin: 0;
        color: #7f8c8d;
        font-size: 13px;
    }
`;

const ExpenseBreakdownItem = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px dashed #f0f0f0;
    
    .category {
        font-weight: 700;
        color: #34495e;
        display: flex;
        align-items: center;
        gap: 8px;
    }
    
    .amount {
        font-weight: 800;
        color: #2c3e50;
    }
`;

const MediaGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
    margin-top: 15px;
`;

const MediaThumbnail = styled.div`
    width: 100%;
    aspect-ratio: 4/3;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #ddd;
    cursor: pointer;
    position: relative;
    
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

export default function NectarHiveMigrations({ apiaryId }) {
    const isGlobal = !apiaryId;
    const [movements, setMovements] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMigrationExpenses, setSelectedMigrationExpenses] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    // Log Expense Modal states
    const [isLogExpenseModalVisible, setIsLogExpenseModalVisible] = useState(false);
    const [selectedMovementForExpense, setSelectedMovementForExpense] = useState(null);
    const [vehicleCost, setVehicleCost] = useState(0);
    const [labourCost, setLabourCost] = useState(0);
    const [otherCost, setOtherCost] = useState(0);
    const [expenseRemarks, setExpenseRemarks] = useState('');
    const [expenseSaving, setExpenseSaving] = useState(false);

    // Edit Migration modal states
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editingMigration, setEditingMigration] = useState(null);
    const [supervisorsList, setSupervisorsList] = useState([]);
    const [editLead, setEditLead] = useState(undefined);
    const [editTeam, setEditTeam] = useState([]);
    const [editSaving, setEditSaving] = useState(false);

    // Filters: Monika explicitly asked for "last 12 months" by default
    const [dateRange, setDateRange] = useState([moment().subtract(12, 'months'), moment()]);
    const [selectedFromApiary, setSelectedFromApiary] = useState(undefined);
    const [selectedToApiary, setSelectedToApiary] = useState(undefined);

    const loadData = async () => {
        setLoading(true);
        try {
            const [movementsRes, expensesRes, supervisorsRes] = await Promise.all([
                apiaryId ? getMovements(apiaryId) : getAllMovements(),
                getAllExpensesGlobal(),
                getSupervisors()
            ]);

            // Monika requested "review all migrations that have happened till date"
            const migrationPurposeList = (movementsRes || []).filter(m => m.purpose === 'MIGRATION');
            setMovements(migrationPurposeList);
            setExpenses(expensesRes || []);
            setSupervisorsList(supervisorsRes || []);
        } catch (error) {
            console.error("Failed to load migration portal records:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [apiaryId]);

    // Map and enrich movements with total linked expense amount
    const enrichedMovements = useMemo(() => {
        return movements.map(m => {
            const linkedExpenses = expenses.filter(exp => exp.movement_id === m.movement_id);
            const totalExpense = linkedExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);

            // Safe Flora name extraction
            let floraName = 'N/A';
            const flora = m.to_apiary_flora;
            if (flora) {
                if (flora.flower) {
                    if (Array.isArray(flora.flower)) {
                        floraName = flora.flower.map(f => f.plant || f.localName).filter(Boolean).join(', ');
                    } else if (typeof flora.flower === 'object') {
                        floraName = flora.flower.plant || flora.flower.localName || 'N/A';
                    } else if (typeof flora.flower === 'string') {
                        floraName = flora.flower;
                    }
                }
                if (flora.type) {
                    floraName += ` (${flora.type})`;
                }
            }

            return {
                ...m,
                totalExpense,
                expensesList: linkedExpenses,
                floraName
            };
        });
    }, [movements, expenses]);

    // Unique options for From Apiary filter
    const fromApiariesOptions = useMemo(() => {
        const map = new Map();
        enrichedMovements.forEach(m => {
            const id = m.from_apiary_id || 'FIELD_FACTORY';
            const name = m.from_apiary_name || 'Field / Factory';
            if (!map.has(id)) {
                map.set(id, name);
            }
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [enrichedMovements]);

    // Unique options for To Apiary filter
    const toApiariesOptions = useMemo(() => {
        const map = new Map();
        enrichedMovements.forEach(m => {
            if (m.to_apiary_id) {
                map.set(m.to_apiary_id, m.to_apiary_name || m.to_apiary_id);
            }
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [enrichedMovements]);

    // Apply Client-Side filters
    const filteredMovements = useMemo(() => {
        return enrichedMovements.filter(m => {
            // 1. Date Range
            if (dateRange && dateRange[0] && dateRange[1]) {
                const migrationDate = moment(m.movement_date);
                if (!migrationDate.isBetween(dateRange[0], dateRange[1], 'day', '[]')) {
                    return false;
                }
            }

            // 2. From Apiary Filter
            if (selectedFromApiary) {
                const fromId = m.from_apiary_id || 'FIELD_FACTORY';
                if (fromId !== selectedFromApiary) {
                    return false;
                }
            }

            // 3. To Apiary Filter
            if (selectedToApiary) {
                if (m.to_apiary_id !== selectedToApiary) {
                    return false;
                }
            }

            return true;
        });
    }, [enrichedMovements, dateRange, selectedFromApiary, selectedToApiary]);

    const handleShowExpenseDetails = (row) => {
        setSelectedMigrationExpenses(row);
        setIsModalVisible(true);
    };

    const handleOpenLogExpenseModal = (row) => {
        setSelectedMovementForExpense(row);

        let vehicle = 0;
        let labour = 0;
        let other = 0;
        let remarksText = '';

        if (row.expensesList && row.expensesList.length > 0) {
            row.expensesList.forEach(exp => {
                if (exp.category === 'TRANSPORT') vehicle = exp.amount || 0;
                if (exp.category === 'LABOUR') labour = exp.amount || 0;
                if (exp.category === 'OTHER') other = exp.amount || 0;
                if (exp.remarks && !remarksText) remarksText = exp.remarks;
            });
        }

        setVehicleCost(vehicle);
        setLabourCost(labour);
        setOtherCost(other);
        setExpenseRemarks(remarksText);
        setIsLogExpenseModalVisible(true);
    };

    const handleSubmitExpense = async () => {
        setExpenseSaving(true);
        try {
            const movementId = selectedMovementForExpense.movement_id;
            const expenseDate = selectedMovementForExpense.movement_date;

            const expensesToSave = [
                { category: 'TRANSPORT', amount: vehicleCost || 0, movement_id: movementId, apiary_id: selectedMovementForExpense.from_apiary_id || null, expense_date: expenseDate, incurred_by_role: 'BOLT', remarks: expenseRemarks },
                { category: 'LABOUR', amount: labourCost || 0, movement_id: movementId, apiary_id: selectedMovementForExpense.from_apiary_id || null, expense_date: expenseDate, incurred_by_role: 'BOLT', remarks: expenseRemarks },
                { category: 'OTHER', amount: otherCost || 0, movement_id: movementId, apiary_id: selectedMovementForExpense.from_apiary_id || null, expense_date: expenseDate, incurred_by_role: 'BOLT', remarks: expenseRemarks }
            ];

            for (const exp of expensesToSave) {
                await addExpense(exp);
            }

            Modal.success({
                title: 'Success',
                content: 'Expenses logged successfully!',
            });
            setIsLogExpenseModalVisible(false);
            loadData();
        } catch (error) {
            console.error('Failed to log expenses:', error);
            Modal.error({
                title: 'Error',
                content: 'Failed to log expenses. Please try again.',
            });
        } finally {
            setExpenseSaving(false);
        }
    };

    const handleOpenEditModal = (row) => {
        setEditingMigration(row);
        setEditLead(row.display_lead?._id || row.display_lead || row.handled_by?._id || row.handled_by || undefined);
        setEditTeam(Array.isArray(row.migration_team) ? row.migration_team.map(u => u._id || u) : []);
        setIsEditModalVisible(true);
    };

    const handleSaveEdit = async () => {
        if (!editLead) {
            Modal.error({
                title: 'Required Field',
                content: 'Please select a migration lead.',
            });
            return;
        }

        setEditSaving(true);
        try {
            // Note: display_lead is sent to update the assigned Migration Lead.
            // handled_by (original app creator) remains SAFE AND UNTOUCHED in DB.
            await updateMovement(editingMigration._id, {
                display_lead: editLead,
                migration_team: editTeam
            });

            Modal.success({
                title: 'Success',
                content: 'Migration lead and team updated successfully!',
            });
            setIsEditModalVisible(false);
            loadData();
        } catch (error) {
            console.error('Failed to update migration details:', error);
            Modal.error({
                title: 'Error',
                content: 'Failed to update migration details. Please try again.',
            });
        } finally {
            setEditSaving(false);
        }
    };

    const handleDeleteMovement = (row) => {
        const displayId = row.display_id || (row._id ? `MIG-${row._id.toString().slice(-6).toUpperCase()}` : row.movement_id);
        Modal.confirm({
            title: 'Delete Migration Record',
            content: `Are you sure you want to delete migration ${displayId}? This record will be soft-deleted.`,
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await deleteMovementRecord(row._id || row.movement_id);
                    Modal.success({
                        title: 'Deleted',
                        content: 'Migration record deleted successfully.',
                    });
                    loadData();
                } catch (err) {
                    console.error('Failed to delete migration record:', err);
                    Modal.error({
                        title: 'Error',
                        content: 'Failed to delete migration record. Please try again.',
                    });
                }
            }
        });
    };

    const columns = [
        {
            title: 'Migration ID',
            dataIndex: 'movement_id',
            key: 'movement_id',
            render: (text, row) => {
                const shortId = row.display_id || (row._id ? `MIG-${row._id.toString().slice(-6).toUpperCase()}` : text);
                return (
                    <span
                        title={text}
                        style={{ fontFamily: 'monospace', fontWeight: 600, cursor: 'help', borderBottom: '1px dotted #ccc' }}
                    >
                        {shortId}
                    </span>
                );
            }
        },
        {
            title: 'Date & Time',
            dataIndex: 'movement_date',
            key: 'movement_date',
            render: val => val ? moment(val).format('DD MMM YYYY, hh:mm A') : 'N/A',
            sorter: (a, b) => new Date(a.movement_date) - new Date(b.movement_date)
        },
        {
            title: 'From Apiary',
            dataIndex: 'from_apiary_name',
            key: 'from_apiary_name',
            render: (text, row) => (
                row.from_apiary_id ? (
                    <Link to={`/nector-apiaries/${row.from_apiary_id}`} style={{ color: '#ff8701', fontWeight: 700 }}>
                        {text || 'Field / Factory'}
                    </Link>
                ) : (
                    text || 'Field / Factory'
                )
            )
        },
        {
            title: 'To Apiary',
            dataIndex: 'to_apiary_name',
            key: 'to_apiary_name',
            render: (text, row) => (
                row.to_apiary_id ? (
                    <Link to={`/nector-apiaries/${row.to_apiary_id}`} style={{ color: '#ff8701', fontWeight: 700 }}>
                        {text}
                    </Link>
                ) : (
                    text
                )
            )
        },
        {
            title: 'Distance',
            dataIndex: 'distance',
            key: 'distance',
            align: 'center',
            render: val => val != null ? `${val} km` : 'N/A',
            sorter: (a, b) => (a.distance || 0) - (b.distance || 0)
        },
        {
            title: 'No. of Hives',
            dataIndex: 'hives_moved',
            key: 'hives_moved',
            align: 'center',
            render: val => <span style={{ fontWeight: 800, color: '#2c3e50' }}>{val}</span>,
            sorter: (a, b) => a.hives_moved - b.hives_moved
        },
        {
            title: 'Lead',
            dataIndex: 'display_lead',
            key: 'display_lead',
            // display_lead: assigned lead edited from web. handled_by: original app creator (SAFE & UNTOUCHED)
            render: (val, row) => row.display_lead?.name || row.handled_by?.name || row.created_by?.name || 'N/A'
        },
        {
            title: 'Migration Team',
            dataIndex: 'migration_team',
            key: 'migration_team',
            render: val => (Array.isArray(val) && val.length > 0)
                ? val.map(u => u?.name).filter(Boolean).join(', ')
                : 'None'
        },
        {
            title: 'Crop / Flora',
            key: 'crop_flora',
            render: (text, row) => {
                const parseFlora = (flora) => {
                    if (!flora) return 'N/A';
                    const typeText = flora.type ? ` (${flora.type})` : '';

                    let flowers = [];
                    if (Array.isArray(flora.flower)) {
                        flowers = flora.flower.map(f => {
                            if (!f) return '';
                            if (f.plant && typeof f.plant === 'object') {
                                return f.plant.name || f.plant.localName || f.localName || '';
                            }
                            return f.plant || f.localName || (typeof f === 'string' ? f : '');
                        }).filter(Boolean);
                    } else if (flora.flower && typeof flora.flower === 'object') {
                        const f = flora.flower;
                        let singleFlower = '';
                        if (f.plant && typeof f.plant === 'object') {
                            singleFlower = f.plant.name || f.plant.localName || f.localName || '';
                        } else {
                            singleFlower = f.plant || f.localName || '';
                        }
                        if (singleFlower) flowers.push(singleFlower);
                    } else if (typeof flora.flower === 'string') {
                        flowers.push(flora.flower);
                    }

                    const flowerText = flowers.join(', ');
                    return flowerText ? `${flowerText}${typeText}` : 'N/A';
                };

                const fromFloraText = parseFlora(row.from_apiary_flora);
                const toFloraText = parseFlora(row.to_apiary_flora);

                return (
                    <div style={{ fontSize: 12 }}>
                        <div><strong>From:</strong> {fromFloraText}</div>
                        <div><strong>To:</strong> {toFloraText}</div>
                    </div>
                );
            }
        },
        {
            title: 'Migration Expense',
            dataIndex: 'expense_summary',
            key: 'expense_summary',
            align: 'right',
            render: (val, row) => (
                row.totalExpense > 0 ? (
                    <ExpenseLink onClick={() => handleShowExpenseDetails(row)}>
                        ₹ {row.totalExpense.toLocaleString('en-IN')}
                    </ExpenseLink>
                ) : (
                    '₹ 0'
                )
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (text, row) => (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <Button
                        type="link"
                        style={{ color: '#ff8701', fontWeight: 600, padding: 0 }}
                        onClick={() => handleOpenLogExpenseModal(row)}
                    >
                        Log Expense
                    </Button>
                    <Button
                        type="link"
                        style={{ color: '#0984e3', fontWeight: 600, padding: 0 }}
                        onClick={() => handleOpenEditModal(row)}
                    >
                        Edit Lead/Team
                    </Button>
                    <Button
                        type="link"
                        style={{ color: '#e74c3c', fontWeight: 600, padding: 0 }}
                        onClick={() => handleDeleteMovement(row)}
                    >
                        Delete
                    </Button>
                </div>
            )
        }
    ];

    return (
        <MainContainer isGlobal={isGlobal}>
            <HeaderSection>
                <h2>
                    Migration Portal
                    <span className="badge-count">{filteredMovements.length} Migrations</span>
                </h2>
                <Button type="primary" style={{ backgroundColor: '#ff8701', borderColor: '#ff8701' }} onClick={loadData}>
                    <Icon type="reload" /> Refresh
                </Button>
            </HeaderSection>

            {/* Filter Panel */}
            <FilterCard>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={7}>
                        <div style={{ marginBottom: 6, fontWeight: 700, color: '#7f8c8d', fontSize: 13 }}>Date Range Filter:</div>
                        <RangePicker
                            value={dateRange}
                            onChange={dates => setDateRange(dates)}
                            style={{ width: '100%' }}
                            ranges={{
                                'Last 30 Days': [moment().subtract(30, 'days'), moment()],
                                'Last 6 Months': [moment().subtract(6, 'months'), moment()],
                                'Last 12 Months': [moment().subtract(12, 'months'), moment()],
                                'This Year': [moment().startOf('year'), moment()]
                            }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <div style={{ marginBottom: 6, fontWeight: 700, color: '#7f8c8d', fontSize: 13 }}>From Apiary:</div>
                        <Select
                            allowClear
                            showSearch
                            placeholder="All From Apiaries"
                            optionFilterProp="children"
                            value={selectedFromApiary}
                            onChange={val => setSelectedFromApiary(val)}
                            filterOption={(input, option) =>
                                String(option.props.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            style={{ width: '100%' }}
                        >
                            {fromApiariesOptions.map(item => (
                                <Option key={item.id} value={item.id}>{item.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <div style={{ marginBottom: 6, fontWeight: 700, color: '#7f8c8d', fontSize: 13 }}>To Apiary:</div>
                        <Select
                            allowClear
                            showSearch
                            placeholder="All To Apiaries"
                            optionFilterProp="children"
                            value={selectedToApiary}
                            onChange={val => setSelectedToApiary(val)}
                            filterOption={(input, option) =>
                                String(option.props.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            style={{ width: '100%' }}
                        >
                            {toApiariesOptions.map(item => (
                                <Option key={item.id} value={item.id}>{item.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={5} style={{ textAlign: 'right', marginTop: 18 }}>
                        <Button onClick={() => {
                            setDateRange([moment().subtract(12, 'months'), moment()]);
                            setSelectedFromApiary(undefined);
                            setSelectedToApiary(undefined);
                        }}>
                            Reset Filters
                        </Button>
                    </Col>
                </Row>
            </FilterCard>

            {/* Table wrapper */}
            <Spin spinning={loading} size="large" tip="Loading migrations...">
                <TableWrapper>
                    {filteredMovements.length > 0 ? (
                        <Table
                            columns={columns}
                            dataSource={filteredMovements}
                            rowKey="_id"
                            pagination={{
                                defaultPageSize: 100,
                                showSizeChanger: true,
                                pageSizeOptions: ['25', '50', '100', '200', '500', '1000']
                            }}
                        />
                    ) : (
                        <Empty description="No migrations found within the selected date range" />
                    )}
                </TableWrapper>
            </Spin>

            {/* Expense Breakdown details modal */}
            {selectedMigrationExpenses && (
                <Modal
                    visible={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    footer={[
                        <Button key="close" type="primary" style={{ backgroundColor: '#ff8701', borderColor: '#ff8701' }} onClick={() => setIsModalVisible(false)}>
                            Close
                        </Button>
                    ]}
                    width={500}
                >
                    <DetailModalHeader>
                        <h3>Migration Expense Breakdown</h3>
                        <p>ID: {selectedMigrationExpenses.movement_id}</p>
                        <p>Date: {moment(selectedMigrationExpenses.movement_date).format('DD MMM YYYY')}</p>
                    </DetailModalHeader>

                    <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 13, color: '#95a5a6', textTransform: 'uppercase', fontWeight: 700, marginBottom: 10 }}>Categories & Costs</div>
                        {selectedMigrationExpenses.expensesList.map(exp => (
                            <ExpenseBreakdownItem key={exp._id}>
                                <span className="category">
                                    <Icon type="tag" style={{ color: '#ff8701' }} />
                                    {exp.category}
                                    {exp.remarks && <span style={{ fontWeight: 400, fontSize: 11, color: '#7f8c8d', marginLeft: 5 }}>({exp.remarks})</span>}
                                </span>
                                <span className="amount">₹ {exp.amount?.toLocaleString('en-IN')}</span>
                            </ExpenseBreakdownItem>
                        ))}

                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0', borderTop: '2px solid #eaeaea', marginTop: 10, fontSize: 16 }}>
                            <strong style={{ color: '#2c3e50' }}>Total Cost:</strong>
                            <strong style={{ color: '#ff8701' }}>₹ {selectedMigrationExpenses.totalExpense?.toLocaleString('en-IN')}</strong>
                        </div>
                    </div>

                    {/* Receipts/Proof gallery */}
                    {selectedMigrationExpenses.expensesList.some(exp => exp.proof_urls && exp.proof_urls.length > 0) && (
                        <div>
                            <div style={{ fontSize: 13, color: '#95a5a6', textTransform: 'uppercase', fontWeight: 700, marginBottom: 10 }}>Uploaded Receipt Proofs</div>
                            <MediaGrid>
                                {selectedMigrationExpenses.expensesList.flatMap(exp => exp.proof_urls || []).map((url, i) => (
                                    <MediaThumbnail key={i} onClick={() => window.open(url, '_blank')}>
                                        <img src={url} alt={`Receipt Proof ${i + 1}`} onError={(e) => { e.target.src = 'https://placehold.co/400x300?text=Receipt+Error'; }} />
                                    </MediaThumbnail>
                                ))}
                            </MediaGrid>
                        </div>
                    )}
                </Modal>
            )}

            {/* Log Expense Modal */}
            <Modal
                title="Log Migration Expenses"
                visible={isLogExpenseModalVisible}
                onCancel={() => setIsLogExpenseModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsLogExpenseModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={expenseSaving}
                        style={{ backgroundColor: '#ff8701', borderColor: '#ff8701' }}
                        onClick={handleSubmitExpense}
                    >
                        Save Expenses
                    </Button>
                ]}
                width={500}
            >
                {selectedMovementForExpense && (
                    <div style={{ marginBottom: 20 }}>
                        <p><strong>Migration ID:</strong> {selectedMovementForExpense.movement_id}</p>
                        <p><strong>Route:</strong> {selectedMovementForExpense.from_apiary_name} → {selectedMovementForExpense.to_apiary_name}</p>
                        <p><strong>Hives Moved:</strong> {selectedMovementForExpense.hives_moved}</p>
                    </div>
                )}
                <Form layout="vertical">
                    <Form.Item label="Vehicle Cost (INR)">
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            value={vehicleCost}
                            onChange={val => setVehicleCost(val)}
                        />
                    </Form.Item>
                    <Form.Item label="Labour Cost (INR)">
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            value={labourCost}
                            onChange={val => setLabourCost(val)}
                        />
                    </Form.Item>
                    <Form.Item label="Others Cost (INR)">
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            value={otherCost}
                            onChange={val => setOtherCost(val)}
                        />
                    </Form.Item>
                    <Form.Item label="Remarks / Notes">
                        <Input.TextArea
                            rows={4}
                            value={expenseRemarks}
                            onChange={e => setExpenseRemarks(e.target.value)}
                            placeholder="Enter remarks..."
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit Lead & Team Modal */}
            <Modal
                title="Edit Migration Lead & Team"
                visible={isEditModalVisible}
                onCancel={() => setIsEditModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsEditModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={editSaving}
                        style={{ backgroundColor: '#ff8701', borderColor: '#ff8701' }}
                        onClick={handleSaveEdit}
                    >
                        Save Changes
                    </Button>
                ]}
                width={500}
            >
                {editingMigration && (
                    <div style={{ marginBottom: 20 }}>
                        <p><strong>Migration ID:</strong> {editingMigration.movement_id}</p>
                        <p><strong>Route:</strong> {editingMigration.from_apiary_name} → {editingMigration.to_apiary_name}</p>
                        <p><strong>Hives Moved:</strong> {editingMigration.hives_moved}</p>
                    </div>
                )}
                <Form layout="vertical">
                    <Form.Item label="Migration Lead" required>
                        <Select
                            showSearch
                            placeholder="Select Lead"
                            optionFilterProp="children"
                            value={editLead}
                            onChange={val => setEditLead(val)}
                            filterOption={(input, option) => {
                                const children = option.props.children;
                                const label = Array.isArray(children) ? children.join('') : String(children || '');
                                return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                            }}
                            style={{ width: '100%' }}
                        >
                            {supervisorsList.map(user => (
                                <Option key={user.id} value={user.id}>
                                    {user.name} ({user.role})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Migration Team (Multi-select)">
                        <Select
                            mode="multiple"
                            showSearch
                            placeholder="Select Team Members"
                            optionFilterProp="children"
                            value={editTeam}
                            onChange={val => setEditTeam(val)}
                            filterOption={(input, option) => {
                                const children = option.props.children;
                                const label = Array.isArray(children) ? children.join('') : String(children || '');
                                return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                            }}
                            style={{ width: '100%' }}
                        >
                            {supervisorsList.map(user => (
                                <Option key={user.id} value={user.id}>
                                    {user.name} ({user.role})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </MainContainer>
    );
}
